import React, { useEffect, useMemo, useState } from 'react';
import { File, FileText, Image as ImageIcon, Loader2, Search, Table2, Users } from 'lucide-react';
import { api } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { BRAND, FILE_COLORS } from './icons';

const MODES = {
  image: {
    title: 'Insertar imagen desde Drive',
    empty: 'No hay imágenes en tu Drive',
    accept: /\.(png|jpe?g|gif|bmp|webp|tiff?)$/i,
    Icon: ImageIcon,
    color: FILE_COLORS.image,
  },
  document: {
    title: 'Elegir documento de Drive',
    empty: 'No hay documentos en tu Drive',
    accept: /\.(docx|doc|odt|rtf|txt|pdf)$/i,
    Icon: FileText,
    color: FILE_COLORS.doc,
  },
  spreadsheet: {
    title: 'Datos para la combinación de correspondencia',
    empty: 'No hay hojas de cálculo en tu Drive',
    accept: /\.(xlsx|xls|ods|csv)$/i,
    Icon: Table2,
    color: FILE_COLORS.sheet,
  },
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

/** Selector de archivos de Drive para el editor (imagen, documento para comparar, datos para combinar correspondencia). */
export default function DrivePicker({ mode, onSelect, onCancel }) {
  const cfg = MODES[mode];
  const [q, setQ] = useState('');
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!cfg) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const [mine, shared] = await Promise.all([
          api.get('/drive/files', { params: { view: 'recent', q: q || undefined } }),
          api.get('/drive/files', { params: { view: 'shared', q: q || undefined } }),
        ]);
        const seen = new Set();
        const all = [...(mine.data.items || []), ...(shared.data.items || [])].filter((f) => {
          if (seen.has(f.id) || !cfg.accept.test(f.name || '')) return false;
          seen.add(f.id);
          return true;
        });
        if (!cancelled) setItems(all);
      } catch {
        if (!cancelled) setItems([]);
      }
    };
    const timer = setTimeout(load, q ? 250 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cfg, q]);

  const Icon = cfg?.Icon || File;
  const list = useMemo(() => items || [], [items]);

  return (
    <Dialog open={!!cfg} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg rounded-3xl zo-picker" data-testid="zo-drive-picker">
        <DialogHeader>
          <DialogTitle>{cfg?.title}</DialogTitle>
        </DialogHeader>
        <label className="zo-picker-search">
          <Search className="h-4 w-4" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en Drive" autoFocus />
        </label>
        <div className="zo-picker-list">
          {mode === 'spreadsheet' && (
            <button type="button" className="zo-picker-item is-special" onClick={() => onSelect({ source: 'contacts' })} data-testid="zo-picker-contacts">
              <span className="zo-picker-icon" style={{ background: 'var(--good-soft)', color: 'var(--good)' }}><Users className="h-5 w-5" /></span>
              <span className="zo-picker-name">
                <strong>Contactos de {BRAND}</strong>
                <small>Nombre, correo, teléfono y empresa de tus contactos</small>
              </span>
            </button>
          )}
          {items === null && (
            <div className="zo-picker-empty"><Loader2 className="h-5 w-5 animate-spin" /></div>
          )}
          {items !== null && list.length === 0 && <div className="zo-picker-empty">{q ? 'Sin resultados' : cfg?.empty}</div>}
          {list.map((f) => (
            <button key={f.id} type="button" className="zo-picker-item" onClick={() => onSelect({ source: 'drive', id: f.id, name: f.name })}>
              <span className="zo-picker-icon" style={{ background: `${cfg.color}18`, color: cfg.color }}><Icon className="h-5 w-5" /></span>
              <span className="zo-picker-name">
                <strong>{f.name}</strong>
                <small>{formatDate(f.updated_at)}</small>
              </span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
